import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { Save } from 'lucide-react'

import type { CreateInNoteRequest } from '@/lib/api/sorting-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import type { DriedLimit } from '@/components/lots/dried-slip-options'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { LotSourcesSubTable } from '@/components/lots/lot-sources-subtable'
import {
    availableFor,
    driedSlipOption,
    driedSourceOverLimit,
    ownUsage,
    usedElsewhere,
} from '@/components/lots/dried-slip-options'
import { sortingLotOption } from '@/components/lots/lot-option-builders'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import { useCreateSortingInNote } from '@/hooks/use-sorting-lot-query'
import { useSortingLotSource } from '@/components/lots/lot-picker-source'
import { useDriedTeaPurchasesInfinite } from '@/hooks/use-dried-tea-purchase-query'
import { sortingLotApi } from '@/lib/api/sorting-lot-api'
import { useDebounced } from '@/hooks/use-debounced'
import { useTeaGradesList } from '@/hooks/use-tea-grade-query'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const baseItemFields = {
    // Nguồn tầng dòng (bắt buộc ≥1 khi reason=from_sorting_process — kiểm ở
    // superRefine). `source_lot_id` giữ id PHIẾU CÂN KHÔ (tên field dùng chung
    // với LotSourcesSubTable), đổi tên lúc gửi.
    sources: z
        .array(
            z.object({
                source_lot_id: z.string().optional(),
                source_quantity: z.coerce.number().optional(),
            }),
        )
        .optional(),
}

const itemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('new'),
        new_lot_code: z.string().optional(),
        new_lot_grade_id: z
            .string({ required_error: m.form_err_pick_grade() })
            .uuid(m.form_err_pick_grade()),
        quantity: z.coerce
            .number({
                required_error: m.form_err_quantity_required(),
                invalid_type_error: m.form_err_number_invalid(),
            })
            .positive(m.form_err_quantity_positive()),
        ...baseItemFields,
    }),
    z.object({
        kind: z.literal('existing'),
        sorting_lot_id: z
            .string({ required_error: m.form_err_pick_existing_lot() })
            .uuid(m.form_err_pick_existing_lot()),
        quantity: z.coerce
            .number({
                required_error: m.form_err_quantity_required(),
                invalid_type_error: m.form_err_number_invalid(),
            })
            .positive(m.form_err_quantity_positive()),
        ...baseItemFields,
    }),
])

// Nguồn tầng dòng: mỗi dòng khai ≥1 phiếu cân khô + kg (> 0), không trùng
// phiếu trong một dòng, và tổng kg mỗi phiếu trên toàn phiếu nhập không vượt
// phần còn lại (getLimits đọc giới hạn mới nhất lúc validate).
function createSchema(getLimits: () => ReadonlyMap<string, DriedLimit>) {
    return z
        .object({
            reason: z.enum([
                'adjustment',
                'from_sorting_process',
                'return_from_scenting',
            ]),
            notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
            items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
        })
        .superRefine((data, ctx) => {
            if (data.reason !== 'from_sorting_process') return
            data.items.forEach((it, idx) => {
                const sources = it.sources ?? []
                if (sources.length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'sources'],
                        message: m.sl_new_err_source_lot(),
                    })
                    return
                }
                const seen = new Set<string>()
                sources.forEach((src, sIdx) => {
                    const path = ['items', idx, 'sources', sIdx]
                    if (!src.source_lot_id) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [...path, 'source_lot_id'],
                            message: m.sl_new_err_source_lot(),
                        })
                    } else if (seen.has(src.source_lot_id)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [...path, 'source_lot_id'],
                            message: m.sl_new_err_source_dup(),
                        })
                    } else {
                        seen.add(src.source_lot_id)
                    }
                    if (
                        src.source_quantity == null ||
                        src.source_quantity <= 0
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [...path, 'source_quantity'],
                            message: m.sl_new_err_source_qty(),
                        })
                    }
                })
            })
            for (const over of driedSourceOverLimit(data.items, getLimits())) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [
                        'items',
                        over.itemIdx,
                        'sources',
                        over.sourceIdx,
                        'source_quantity',
                    ],
                    message: m.sl_new_err_source_over({
                        code: over.code,
                        qty: formatQty(over.available),
                    }),
                })
            }
        })
}

type FormData = z.infer<ReturnType<typeof createSchema>>
type LotOpt = LotOption

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sorting-warehouse/stock-in-notes/new',
)({
    component: NewPNKPage,
})

function NewPNKPage() {
    const navigate = useNavigate()
    const create = useCreateSortingInNote()

    const handleSubmit = async (
        value: CreateInNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        try {
            const res = await workflow.run(
                () => create.mutateAsync(value),
                (result) => result.data?.id,
            )
            const noteId = res.data?.id
            if (noteId)
                void navigate({
                    to: '/sorting-warehouse/stock-in-notes/$id',
                    params: { id: noteId },
                })
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <SortingLotStockInForm
            isPending={create.isPending}
            onSubmit={handleSubmit}
            onCancel={() =>
                navigate({
                    to: '/sorting-warehouse/stock-in-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function SortingLotStockInForm({
    mode = 'create',
    initialValues,
    title,
    submitLabel,
    seedLotOptions,
    seedSourceOptions,
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    title?: string
    submitLabel?: string
    /** Seed thêm option lô (lô hiện có trong phiếu khi sửa). */
    seedLotOptions?: LotOpt[]
    /** Phiếu cân khô nguồn đã chọn (để sửa hiện đúng dù phiếu đã hết kg). */
    seedSourceOptions?: LotOpt[]
    isPending: boolean
    onSubmit: (
        value: CreateInNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
    onCancel: () => void
}) {
    const workflow = useWorkflowTracking('sorting_stock_in', {
        operation: mode === 'edit' ? 'update' : 'create',
    })
    const isEdit = mode === 'edit'
    const { data: gradesData } = useTeaGradesList()

    // Picker phiếu cân khô nguồn: tìm phía server, chỉ phiếu còn kg, cuộn tải thêm.
    const [sourceSearch, setSourceSearch] = useState('')
    const debouncedSourceSearch = useDebounced(sourceSearch, 300)
    const driedQuery = useDriedTeaPurchasesInfinite({
        only_with_remaining: true,
        search: debouncedSourceSearch || undefined,
    })
    const driedSlips = useMemo(
        () => driedQuery.data?.pages.flatMap((p) => p.items) ?? [],
        [driedQuery.data],
    )

    // Giới hạn đọc lúc validate: ref để schema tạo một lần vẫn thấy số mới.
    const limitsRef = useRef<ReadonlyMap<string, DriedLimit>>(new Map())
    const [schema] = useState(() => createSchema(() => limitsRef.current))

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'from_sorting_process',
            notes: '',
            items: [
                { kind: 'new' as const, new_lot_grade_id: '', quantity: 0 },
            ],
        },
    })

    const reason = useWatch({ control: form.control, name: 'reason' })

    const gradeOptions = useMemo(
        () =>
            (gradesData?.data ?? []).map((g) => ({
                value: g.id,
                label: g.name,
            })),
        [gradesData],
    )
    // Ô "lô có sẵn": tìm server + cuộn tải thêm (nguồn lô dùng chung); lô
    // của phiếu đang sửa làm seed để luôn hiện được.
    const lotSource = useSortingLotSource({ seed: seedLotOptions })
    // Kg của chính phiếu đang sửa đã bị server trừ khỏi `remaining_dry_kg` —
    // cộng lại để người sửa thấy đúng phần được lấy.
    const [ownOriginal] = useState(() =>
        isEdit ? ownUsage(initialValues?.items) : new Map<string, number>(),
    )
    const watchedItems = useWatch({ control: form.control, name: 'items' })
    // Chỉ phiếu có trong trang đã tải mới biết kg còn lại. Phiếu seed (đang
    // dùng ở phiếu sửa) nằm ngoài trang thì KHÔNG đặt giới hạn — coi là 0 sẽ
    // chặn nhầm việc tăng kg; server luôn kiểm lại lúc lưu và lúc duyệt.
    limitsRef.current = useMemo(() => {
        const map = new Map<string, DriedLimit>()
        for (const d of driedSlips)
            map.set(d.id, {
                code: d.code,
                available: availableFor(
                    d.id,
                    d.remaining_dry_kg ?? 0,
                    ownOriginal,
                    new Map(),
                ),
            })
        return map
    }, [driedSlips, ownOriginal])
    // Option theo TỪNG dòng: trừ phần kg phiếu đó đã khai ở các dòng khác.
    const sourceOptionsFor = (idx: number): LotOpt[] => {
        const elsewhere = usedElsewhere(watchedItems, idx)
        const seeds = new Map(
            (seedSourceOptions ?? []).map((o) => [o.value, o]),
        )
        const opts: LotOpt[] = driedSlips.map((d) =>
            driedSlipOption(
                d,
                availableFor(
                    d.id,
                    d.remaining_dry_kg ?? 0,
                    ownOriginal,
                    elsewhere,
                ),
            ),
        )
        for (const [id, seed] of seeds) {
            if (opts.some((o) => o.value === id)) continue
            // Không biết kg còn lại → không hiện "còn X" và nút "Lấy hết".
            opts.push({ ...seed, available: undefined, unit: 'kg' })
        }
        return opts
    }

    const [scannedLots, setScannedLots] = useState<Map<string, LotOpt>>(
        new Map(),
    )
    const scannedList = useMemo(
        () => Array.from(scannedLots.values()),
        [scannedLots],
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await sortingLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, sortingLotOption(l))
                return next
            })
        } catch {
            // ignore
        }
    }

    useEffect(() => {
        const subscription = form.watch((_values, { name }) => {
            if (!name) return
            workflow.interact(name.startsWith('items') ? 'items' : 'details')
            if (name.endsWith('_lot_id') || name === 'reason')
                workflow.step('selection_changed')
        })
        return () => subscription.unsubscribe()
    }, [form, workflow])

    const onSubmitForm = form.handleSubmit(
        (data) => {
            const isChain = data.reason === 'from_sorting_process'
            const items = data.items.map((it) => {
                const src = isChain
                    ? {
                          sources: (it.sources ?? [])
                              .filter((s) => s.source_lot_id)
                              .map((s) => ({
                                  source_dried_tea_purchase_id:
                                      s.source_lot_id as string,
                                  source_quantity: Number(
                                      s.source_quantity ?? 0,
                                  ),
                              })),
                      }
                    : {}
                return it.kind === 'new'
                    ? {
                          new_lot_grade_id: it.new_lot_grade_id,
                          new_lot_code: it.new_lot_code || undefined,
                          quantity: Number(it.quantity),
                          ...src,
                      }
                    : {
                          sorting_lot_id: it.sorting_lot_id,
                          quantity: Number(it.quantity),
                          ...src,
                      }
            })
            onSubmit(
                {
                    reason: data.reason,
                    notes: data.notes,
                    items,
                },
                workflow,
            )
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <PageBackButton onBack={onCancel} />
                    <h1 className="text-xl font-semibold">
                        {title ?? m.sl_new_pnk_title()}
                    </h1>
                </div>
            </div>

            <FormProvider {...form}>
                <form onSubmit={onSubmitForm} className="space-y-6 max-w-4xl">
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4 [&>*]:min-w-0">
                        <div>
                            <Label>{m.sl_new_reason_in()}</Label>
                            <select
                                className="mt-1 h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60"
                                disabled={isEdit}
                                {...form.register('reason')}
                            >
                                <option value="from_sorting_process">
                                    {m.sl_new_reason_in_from_pxk()}
                                </option>
                                <option value="return_from_scenting">
                                    {m.sl_in_reason_return_from_scenting()}
                                </option>
                                <option value="adjustment">
                                    {m.sl_new_reason_in_adjustment()}
                                </option>
                            </select>
                        </div>
                        <div>
                            <Label>{m.sl_new_notes()}</Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                    </div>

                    <LotItemsField
                        mode="in"
                        lotSource={lotSource}
                        categoryOptions={gradeOptions}
                        categoryLabel={m.sl_lot_grade()}
                        allowNewLot={reason !== 'return_from_scenting'}
                        scannedLots={scannedList}
                        renderExtraPerItem={
                            reason === 'from_sorting_process'
                                ? (idx) => (
                                      <LotSourcesSubTable
                                          idx={idx}
                                          options={sourceOptionsFor(idx)}
                                          onSearchChange={setSourceSearch}
                                          onLoadMore={() =>
                                              void driedQuery.fetchNextPage()
                                          }
                                          hasMore={driedQuery.hasNextPage}
                                          labels={{
                                              title: m.sl_new_sources_title(),
                                              empty: m.sl_new_sources_empty(),
                                              add: m.sl_new_source_add(),
                                              remove: m.sl_new_source_remove(),
                                              lotPlaceholder:
                                                  m.sl_new_source_lot_placeholder(),
                                              qtyLabel:
                                                  m.sl_new_source_qty_label(),
                                          }}
                                      />
                                  )
                                : undefined
                        }
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'sorting-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue(
                                        'items',
                                        [
                                            ...current,
                                            {
                                                kind: 'existing',
                                                sorting_lot_id: lotId,
                                                quantity: 0,
                                            },
                                        ],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: false,
                                        },
                                    )
                                    void fetchAndCacheLot(lotId)
                                }}
                            />
                        }
                    />

                    {reason === 'return_from_scenting' && (
                        <p className="text-xs text-muted-foreground">
                            {m.sl_in_reason_return_from_scenting_hint()}
                        </p>
                    )}

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ??
                                (isPending
                                    ? m.sl_new_submitting()
                                    : m.sl_new_submit_pnk())}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
